// Tree-Sync: parentNodeId (am MindmapNodeObj) ist Single Source of Truth.
// `tree-parent`-Links in yLinks werden automatisch daraus reconciliert. Das ergibt
// Cascade-Behavior fuers visuelle Connector-Layer ohne dass das Frontend Tree-Edges
// separat pflegen muss. Sync laeuft idempotent — `syncingRef` guarded gegen den
// Observer-Loop wenn die Sync-Routine selbst yLinks aendert.

import { type Link, type MindmapNodeObj, isCanvasObject, isLink } from "@ms-whiteboard/shared";
import { useEffect, useRef } from "react";
import type * as Y from "yjs";

export interface UseTreeSyncOptions {
  yMap: Y.Map<unknown> | null;
  yLinks: Y.Array<Link> | null;
  userId: string | null;
  newId: () => string;
}

export function useTreeSync({ yMap, yLinks, userId, newId }: UseTreeSyncOptions): void {
  const syncingRef = useRef(false);

  useEffect(() => {
    if (!yMap || !yLinks || !userId) return;

    const reconcile = (): void => {
      if (syncingRef.current) return;
      const plan = computeTreeSyncPlan(yMap, yLinks);
      if (plan.missing.length === 0 && plan.staleIndices.length === 0) return;
      const doc = yLinks.doc;
      const op = (): void => {
        // Erst loeschen (Indizes ab Ende), dann pushen — keine Index-Verschiebung.
        const sortedIdx = [...plan.staleIndices].sort((a, b) => b - a);
        for (const idx of sortedIdx) yLinks.delete(idx, 1);
        if (plan.missing.length > 0) {
          const ts = Date.now();
          const created: Link[] = plan.missing.map((m) => ({
            id: newId(),
            from: m.from,
            to: m.to,
            type: "tree-parent",
            createdBy: userId,
            createdAt: ts,
          }));
          yLinks.push(created);
        }
      };
      syncingRef.current = true;
      try {
        if (doc) doc.transact(op);
        else op();
      } finally {
        syncingRef.current = false;
      }
    };

    reconcile();
    yMap.observe(reconcile);
    yLinks.observe(reconcile);
    return () => {
      yMap.unobserve(reconcile);
      yLinks.unobserve(reconcile);
    };
  }, [yMap, yLinks, userId, newId]);
}

export interface TreeSyncPlan {
  readonly missing: ReadonlyArray<{ readonly from: string; readonly to: string }>;
  readonly staleIndices: readonly number[];
}

/** Pure Function fuer Tests + Hook-Reuse. */
export function computeTreeSyncPlan(yMap: Y.Map<unknown>, yLinks: Y.Array<Link>): TreeSyncPlan {
  // expected: Map<childId, parentId> — nur fuer mindmap-nodes mit parentNodeId.
  const expected = new Map<string, string>();
  const existingIds = new Set<string>();
  for (const value of yMap.values()) {
    if (!isCanvasObject(value)) continue;
    existingIds.add(value.id);
    if (value.type !== "mindmap-node") continue;
    const node = value as MindmapNodeObj;
    if (node.parentNodeId) expected.set(node.id, node.parentNodeId);
  }

  // actual: alle tree-parent-Links inkl. Index fuer Loesch-Ops.
  const linksArr = yLinks.toArray();
  const seenEdges = new Set<string>();
  const staleIndices: number[] = [];
  for (let i = 0; i < linksArr.length; i += 1) {
    const raw = linksArr[i];
    if (!isLink(raw) || raw.type !== "tree-parent") continue;
    const childId = raw.to;
    const parentId = raw.from;
    const expectedParent = expected.get(childId);
    const edgeKey = `${parentId}->${childId}`;
    const childExists = existingIds.has(childId);
    const parentExists = existingIds.has(parentId);
    if (!childExists || !parentExists || expectedParent !== parentId || seenEdges.has(edgeKey)) {
      staleIndices.push(i);
      continue;
    }
    seenEdges.add(edgeKey);
  }

  const missing: Array<{ from: string; to: string }> = [];
  for (const [childId, parentId] of expected) {
    if (!seenEdges.has(`${parentId}->${childId}`)) {
      missing.push({ from: parentId, to: childId });
    }
  }

  return { missing, staleIndices };
}
